import React, { useEffect, useRef, useState } from 'react';
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Monitor,
  MonitorOff,
  Users,
  MessageSquare,
  PhoneOff,
  ArrowUp,
  MoreHorizontal,
  Hand,
  Smile,
  ListChecks,
} from 'lucide-react';
import MoreOptionsMenu from './MoreOptionsMenu.tsx';

interface ControlsProps {
  isVideoEnabled: boolean;
  isAudioEnabled: boolean;
  isScreenSharing: boolean;
  isRecording: boolean;
  isSavingRecording: boolean;
  isHandRaised: boolean;
  isFullscreen: boolean;
  transparentMode: boolean;
  showParticipants: boolean;
  showChat: boolean;
  showAgenda?: boolean;
  hasAgenda?: boolean;
  unreadChatCount?: number;
  showReactions: boolean;
  onToggleVideo: () => void;
  onToggleAudio: () => void;
  onToggleScreenShare: () => void;
  onToggleParticipants: () => void;
  onToggleChat: () => void;
  onToggleAgenda?: () => void;
  onToggleReactions: () => void;
  onToggleHandRaise: () => void;
  onToggleRecording: () => void;
  onToggleFullscreen: () => void;
  onOpenBackgroundEffects: () => void;
  onOpenSettings: () => void;
  onToggleTransparent: () => void;
  onLeaveMeeting: () => void;
  /** Host: tutanak */
  canToggleMinutes?: boolean;
  minutesEnabled?: boolean;
  onToggleMinutes?: () => void;
}

const Controls: React.FC<ControlsProps> = ({
  isVideoEnabled,
  isAudioEnabled,
  isScreenSharing,
  isRecording,
  isSavingRecording,
  isHandRaised,
  isFullscreen,
  transparentMode,
  showParticipants,
  showChat,
  showAgenda = false,
  hasAgenda = false,
  unreadChatCount = 0,
  showReactions,
  onToggleVideo,
  onToggleAudio,
  onToggleScreenShare,
  onToggleParticipants,
  onToggleChat,
  onToggleAgenda,
  onToggleReactions,
  onToggleHandRaise,
  onToggleRecording,
  onToggleFullscreen,
  onOpenBackgroundEffects,
  onOpenSettings,
  onToggleTransparent,
  onLeaveMeeting,
  canToggleMinutes = false,
  minutesEnabled = false,
  onToggleMinutes,
}) => {
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const desktopMoreMenuRef = useRef<HTMLDivElement>(null);
  const mobileMoreMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMoreMenu) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inDesktop = desktopMoreMenuRef.current?.contains(target);
      const inMobile = mobileMoreMenuRef.current?.contains(target);
      if (!inDesktop && !inMobile) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoreMenu]);

  const btnBase =
    'p-3 rounded-2xl transition min-w-[48px] min-h-[48px] flex items-center justify-center active:scale-95 relative border border-transparent';
  const mobileBtnBase =
    'w-12 h-12 rounded-2xl transition flex items-center justify-center active:scale-95 relative border border-transparent';

  const idleBtn = 'bg-white/[0.08] hover:bg-white/15 text-white border-white/10';
  const offBtn = 'bg-red-500/90 hover:bg-red-600 text-white border-red-400/30 shadow-lg shadow-red-500/20';
  const activeBtn = 'bg-purple-600 hover:bg-purple-500 text-white border-purple-400/40 shadow-lg shadow-purple-500/25';

  return (
    <div className="relative z-50 border-t border-white/10 bg-gradient-to-t from-slate-950/95 via-slate-900/90 to-slate-900/80 backdrop-blur-xl overflow-visible [padding-bottom:max(0.5rem,env(safe-area-inset-bottom))]">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-purple-500/40 to-transparent" />

      {/* Desktop */}
      <div className="mx-auto hidden max-w-5xl items-center justify-center overflow-visible px-4 py-3 md:flex">
        <div className="inline-flex items-center gap-2 rounded-[1.75rem] border border-white/10 bg-black/35 px-3 py-2 shadow-2xl shadow-black/40 backdrop-blur-md">
          <div className="flex items-center gap-1.5 pr-2 border-r border-white/10">
            <button
              onClick={onToggleAudio}
              className={`${btnBase} ${isAudioEnabled ? idleBtn : offBtn}`}
              title={isAudioEnabled ? 'Mikrofonu kapat' : 'Mikrofonu aç'}
              aria-label={isAudioEnabled ? 'Mikrofonu kapat' : 'Mikrofonu aç'}
            >
              {isAudioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
            </button>
            <button
              onClick={onToggleVideo}
              className={`${btnBase} ${isVideoEnabled ? idleBtn : offBtn}`}
              title={isVideoEnabled ? 'Kamerayı kapat' : 'Kamerayı aç'}
              aria-label={isVideoEnabled ? 'Kamerayı kapat' : 'Kamerayı aç'}
            >
              {isVideoEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
            </button>
            <button
              onClick={onToggleScreenShare}
              className={`${btnBase} ${isScreenSharing ? activeBtn : idleBtn}`}
              title={isScreenSharing ? 'Ekran paylaşımını durdur' : 'Ekran paylaş'}
              aria-label={isScreenSharing ? 'Ekran paylaşımını durdur' : 'Ekran paylaş'}
            >
              {isScreenSharing ? (
                <MonitorOff className="w-5 h-5" />
              ) : (
                <div className="relative inline-block">
                  <Monitor className="w-5 h-5" />
                  <ArrowUp className="w-2.5 h-2.5 absolute -top-0.5 -right-0.5 text-purple-300" />
                </div>
              )}
            </button>
          </div>

          <div className="flex items-center gap-1.5 px-1">
            <button
              onClick={onToggleHandRaise}
              className={`${btnBase} ${
                isHandRaised
                  ? 'bg-amber-500 hover:bg-amber-400 text-white border-amber-300/40 shadow-lg shadow-amber-500/25'
                  : idleBtn
              }`}
              title={isHandRaised ? 'Söz istemeyi bırak' : 'Söz iste'}
              aria-label={isHandRaised ? 'Söz istemeyi bırak' : 'Söz iste'}
            >
              <Hand className="w-5 h-5" />
            </button>
            <button
              onClick={onToggleReactions}
              className={`${btnBase} ${showReactions ? activeBtn : idleBtn}`}
              title="Emoji gönder"
              aria-label="Emoji gönder"
            >
              <Smile className="w-5 h-5" />
            </button>
            <button
              onClick={onToggleParticipants}
              className={`${btnBase} ${showParticipants ? activeBtn : idleBtn}`}
              title="Katılımcılar"
              aria-label="Katılımcılar"
            >
              <Users className="w-5 h-5" />
            </button>
            <button
              onClick={onToggleChat}
              className={`${btnBase} relative ${
                showChat
                  ? activeBtn
                  : unreadChatCount > 0
                    ? `${idleBtn} ring-2 ring-purple-500/70`
                    : idleBtn
              }`}
              title="Sohbet"
              aria-label={unreadChatCount > 0 ? `Sohbet (${unreadChatCount} yeni mesaj)` : 'Sohbet'}
            >
              <MessageSquare className="w-5 h-5" />
              {unreadChatCount > 0 && !showChat && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 flex items-center justify-center bg-red-500 text-white text-[11px] font-bold rounded-full border-2 border-slate-900">
                  {unreadChatCount > 9 ? '9+' : unreadChatCount}
                </span>
              )}
            </button>
            {hasAgenda && onToggleAgenda && (
              <button
                onClick={onToggleAgenda}
                className={`${btnBase} ${showAgenda ? activeBtn : idleBtn}`}
                title="Gündem"
                aria-label="Gündem"
              >
                <ListChecks className="w-5 h-5" />
              </button>
            )}
            <div className="relative z-[60]" ref={desktopMoreMenuRef}>
              <button
                onClick={() => setShowMoreMenu((prev) => !prev)}
                className={`${btnBase} ${showMoreMenu || isRecording ? activeBtn : idleBtn}`}
                title="Daha fazla"
                aria-label="Daha fazla seçenekler"
                aria-expanded={showMoreMenu ? 'true' : 'false'}
              >
                <MoreHorizontal className="w-5 h-5" />
                {isRecording && (
                  <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                )}
              </button>
              {showMoreMenu && (
                <MoreOptionsMenu
                  isRecording={isRecording}
                  isSavingRecording={isSavingRecording}
                  isFullscreen={isFullscreen}
                  transparentMode={transparentMode}
                  canToggleMinutes={canToggleMinutes}
                  minutesEnabled={minutesEnabled}
                  onToggleMinutes={onToggleMinutes}
                  onToggleRecording={onToggleRecording}
                  onToggleFullscreen={onToggleFullscreen}
                  onOpenBackgroundEffects={onOpenBackgroundEffects}
                  onOpenSettings={onOpenSettings}
                  onToggleTransparent={onToggleTransparent}
                  onClose={() => setShowMoreMenu(false)}
                />
              )}
            </div>
          </div>

          <div className="pl-2 border-l border-white/10">
            <button
              onClick={onLeaveMeeting}
              className={`${btnBase} bg-gradient-to-br from-red-500 to-rose-600 hover:from-red-400 hover:to-rose-500 text-white border-red-400/30 shadow-lg shadow-red-500/30`}
              title="Toplantıdan ayrıl"
              aria-label="Toplantıdan ayrıl"
            >
              <PhoneOff className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="mx-auto flex max-w-md items-center justify-center px-3 py-2.5 md:hidden">
        <div className="w-full inline-flex items-center justify-between gap-1.5 rounded-[1.5rem] border border-white/10 bg-black/40 px-2 py-1.5 shadow-2xl shadow-black/40 backdrop-blur-md">
          <button
            onClick={onToggleAudio}
            className={`${mobileBtnBase} ${isAudioEnabled ? idleBtn : offBtn}`}
            title={isAudioEnabled ? 'Mikrofonu kapat' : 'Mikrofonu aç'}
            aria-label={isAudioEnabled ? 'Mikrofonu kapat' : 'Mikrofonu aç'}
          >
            {isAudioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
          </button>
          <button
            onClick={onToggleVideo}
            className={`${mobileBtnBase} ${isVideoEnabled ? idleBtn : offBtn}`}
            title={isVideoEnabled ? 'Kamerayı kapat' : 'Kamerayı aç'}
            aria-label={isVideoEnabled ? 'Kamerayı kapat' : 'Kamerayı aç'}
          >
            {isVideoEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
          </button>
          <button
            onClick={onToggleChat}
            className={`${mobileBtnBase} relative ${
              showChat
                ? activeBtn
                : unreadChatCount > 0
                  ? `${idleBtn} ring-2 ring-purple-500/70`
                  : idleBtn
            }`}
            title="Sohbet"
            aria-label={unreadChatCount > 0 ? `Sohbet (${unreadChatCount} yeni mesaj)` : 'Sohbet'}
          >
            <MessageSquare className="w-5 h-5" />
            {unreadChatCount > 0 && !showChat && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full border-2 border-slate-900">
                {unreadChatCount > 9 ? '9+' : unreadChatCount}
              </span>
            )}
          </button>
          {hasAgenda && onToggleAgenda && (
            <button
              onClick={onToggleAgenda}
              className={`${mobileBtnBase} ${showAgenda ? activeBtn : idleBtn}`}
              title="Gündem"
              aria-label="Gündem"
            >
              <ListChecks className="w-5 h-5" />
            </button>
          )}
          <div className="relative z-[60]" ref={mobileMoreMenuRef}>
            <button
              onClick={() => setShowMoreMenu((prev) => !prev)}
              className={`${mobileBtnBase} ${showMoreMenu || isRecording ? activeBtn : idleBtn}`}
              title="Daha fazla"
              aria-label="Daha fazla seçenekler"
              aria-expanded={showMoreMenu ? 'true' : 'false'}
            >
              <MoreHorizontal className="w-5 h-5" />
              {isRecording && (
                <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              )}
            </button>
            {showMoreMenu && (
              <MoreOptionsMenu
                isRecording={isRecording}
                isSavingRecording={isSavingRecording}
                isFullscreen={isFullscreen}
                transparentMode={transparentMode}
                isScreenSharing={isScreenSharing}
                isHandRaised={isHandRaised}
                showReactions={showReactions}
                showParticipants={showParticipants}
                mobileExtras
                canToggleMinutes={canToggleMinutes}
                minutesEnabled={minutesEnabled}
                onToggleMinutes={onToggleMinutes}
                onToggleRecording={onToggleRecording}
                onToggleFullscreen={onToggleFullscreen}
                onOpenBackgroundEffects={onOpenBackgroundEffects}
                onOpenSettings={onOpenSettings}
                onToggleTransparent={onToggleTransparent}
                onToggleScreenShare={onToggleScreenShare}
                onToggleParticipants={onToggleParticipants}
                onToggleReactions={onToggleReactions}
                onToggleHandRaise={onToggleHandRaise}
                onClose={() => setShowMoreMenu(false)}
              />
            )}
          </div>
          <button
            onClick={onLeaveMeeting}
            className={`${mobileBtnBase} bg-gradient-to-br from-red-500 to-rose-600 hover:from-red-400 hover:to-rose-500 text-white border-red-400/30 shadow-lg shadow-red-500/30`}
            title="Toplantıyı bitir"
            aria-label="Toplantıyı bitir"
          >
            <PhoneOff className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Controls;
